import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { Trash2, ArrowLeft } from "lucide-react";
import api from "../Axios/api";
import BackBtn from "../common/BackBtn";

const initialHeader = () => ({
  IssueDate: new Date().toLocaleDateString("en-CA"),
  IssueVoucherNo: "",
  SourceType: "Direct",
  ReferenceId: null,
  FactoryId: "",
  IssueTo: "",
  IssueTag: "Regular",
  IssueRemark: "",
  IssueNarration: "",
});

const initialItem = () => ({
  ItemId: "",
  ItemName: "",
  AvailableQty: "",
  IssuedQty: "",
  PlaceofUse: "",
  ItemRemark: "",
});

const getArrayFromResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const NewConsumptionForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // Safely extract the ID from location.state or URL params
  const issueId = useMemo(() => {
    const state = location.state;
    if (!state) return params.id || null;
    if (typeof state === "string") return state;
    if (typeof state === "object") {
      return state._id || state.id || state.IssueMainId || null;
    }
    return null;
  }, [location.state, params.id]);

  const isViewMode = Boolean(issueId);

  const [formData, setFormData] = useState(initialHeader);
  const [itemForm, setItemForm] = useState(initialItem);
  const [finalData, setFinalData] = useState([]);

  const [factoryOptions, setFactoryOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);

  const [loadingFactories, setLoadingFactories] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedFactory, setSelectedFactory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [loadingStock, setLoadingStock] = useState(false);

  // 1. Fetch Factory Locations
  const fetchFactoryLocations = async () => {
    setLoadingFactories(true);
    try {
      const res = await api.get("/factory-location/get-factory-location");
      const data = getArrayFromResponse(res.data);
      const options = data.map((location) => ({
        value: location._id,
        label: location.fectory_loca_name,
      }));
      setFactoryOptions(options);
    } catch (error) {
      console.error("Error fetching factory locations:", error);
      setFactoryOptions([]);
    } finally {
      setLoadingFactories(false);
    }
  };

  // 2. Fetch Item Categories (only needed in Add Mode)
  const fetchItemCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await api.get("/purchase-item-category/get-name");
      const data = getArrayFromResponse(res.data);
      const options = data.map((category) => ({
        value: category._id,
        label: category.itemcat_title,
      }));
      setCategoryOptions(options);
    } catch (error) {
      console.error("Error fetching item categories:", error);
      setCategoryOptions([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchFactoryLocations();
    if (!isViewMode) {
      fetchItemCategories();
    }
  }, [isViewMode]);

  // 3. Fetch Issue Details for your exact payload { success: true, result: { ... } }
  useEffect(() => {
    if (!issueId) {
      setFormData(initialHeader());
      setItemForm(initialItem());
      setFinalData([]);
      setSelectedFactory(null);
      setSelectedCategory(null);
      setSelectedItem(null);
      return;
    }

    const fetchIssueDetail = async () => {
      setLoadingDetail(true);
      try {
        const res = await api.get(`/inventory/issueDetail/${issueId}`);
        // Specifically targets res.data.result from your payload
        const voucher =
          res.data?.result || res.data?.data || res.data?.issue || res.data;

        if (!voucher) {
          Swal.fire("Error", "No voucher data found for this ID.", "error");
          return;
        }

        let formattedDate = "";
        if (voucher.IssueDate) {
          formattedDate = voucher.IssueDate.includes("T")
            ? voucher.IssueDate.split("T")[0]
            : voucher.IssueDate;
        }

        const factoryId = voucher.FactoryId?._id || voucher.FactoryId || "";
 
        // Set Header fields
        setFormData({
          IssueDate: formattedDate || new Date().toLocaleDateString("en-CA"),
          IssueVoucherNo: voucher.IssueVoucherNo || "",
          SourceType: voucher.SourceType || "Direct",
          ReferenceId: voucher.ReferenceId || null,
          FactoryId: factoryId,
          IssueTo: voucher.IssueTo || "",
          IssueTag: voucher.IssueTag || "Regular",
          IssueRemark: voucher.IssueRemark || "",
          IssueNarration: voucher.IssueNarration || "",
        });

        // Map items array from your payload
        const rawItems =
          voucher.items ||
          voucher.details ||
          voucher.issueDetails ||
          [];

        const mappedItems = rawItems.map((item, index) => {
          const itemId = item.ItemId?._id || item.ItemId || item.itemId || "";
          const itemName = item.itemname 

          return {
            lineId: item._id || item.lineId || `${itemId}-${index}-${Date.now()}`,
            ItemName: itemName,
            AvailableQty: item.AvailableQty ?? "—",
            IssuedQty: Number(item.IssuedQty ?? item.qty ?? 0),
            IssuedAmt: Number(item.IssuedAmt ?? 0),
            PlaceofUse: item.PlaceofUse || item.placeOfUse || "",
            ItemRemark: item.ItemRemark || item.itemRemark || "",
          };
        });

        setFinalData(mappedItems);
      } catch (error) {
        console.error("Error fetching voucher details:", error);
        const message =
          error.response?.data?.message ||
          "Failed to load consumption voucher details.";
        Swal.fire("Error", message, "error");
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchIssueDetail();
  }, [issueId]);

  // Synchronize factory name once factoryOptions are loaded
  useEffect(() => {
    if (isViewMode && formData.FactoryId && factoryOptions.length > 0) {
      const match = factoryOptions.find((f) => f.value === formData.FactoryId);
      if (match && selectedFactory?.label !== match.label) {
        setSelectedFactory(match);
      }
    }
  }, [factoryOptions, formData.FactoryId, isViewMode, selectedFactory]);

  // 4. Fetch Items (Add Mode only)
  const fetchGroupItems = async (catId, factoryId) => {
    if (!catId || !factoryId) {
      setItemOptions([]);
      return;
    }

    setLoadingItems(true);
    try {
      const res = await api.get("/purchase-item/get-item-by-catloc/", {
        params: { catId, factory: factoryId },
      });

      const data = getArrayFromResponse(res.data);
      const options = data.map((item) => ({
        value: item._id,
        label: item.item_title,
        unit: item.item_unit ?? "",
      }));

      setItemOptions(options);
    } catch (error) {
      console.error("Error fetching items:", error);
      setItemOptions([]);
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    if (isViewMode) return;

    setItemOptions([]);
    setSelectedItem(null);
    setItemForm(initialItem());

    if (!selectedFactory?.value || !selectedCategory?.value) {
      return;
    }

    fetchGroupItems(selectedCategory.value, selectedFactory.value);
  }, [selectedFactory, selectedCategory, isViewMode]);

  // 5. Fetch Stock (Add Mode only)
  const fetchAvailableStock = async (itemId, issueDate) => {
    if (!itemId || !issueDate) {
      setItemForm((previous) => ({
        ...previous,
        AvailableQty: "",
      }));
      return;
    }

    setLoadingStock(true);
    try {
      const response = await api.get("/inventory/stockOnDate", {
        params: { itemId, issueDate },
      });

      const availableQty = Number(response.data?.availableQty ?? "");
      setItemForm((previous) => ({
        ...previous,
        AvailableQty: availableQty,
      }));
    } catch (error) {
      console.error("Error fetching available stock:", error);
    } finally {
      setLoadingStock(false);
    }
  };

  useEffect(() => {
    if (isViewMode) return;

    const factoryId = selectedFactory?.value;
    const itemId = selectedItem?.value;
    const issueDate = formData.IssueDate;

    if (!factoryId || !itemId || !issueDate) {
      setItemForm((previous) => ({
        ...previous,
        AvailableQty: 0,
      }));
      return;
    }

    fetchAvailableStock(itemId, issueDate);
  }, [selectedItem, formData.IssueDate, selectedFactory, isViewMode]);

  const handleFactoryChange = (option) => {
    setSelectedFactory(option);
    setFormData((previous) => ({
      ...previous,
      FactoryId: option?.value || "",
    }));
    setSelectedItem(null);
    setItemForm(initialItem());
    setItemOptions([]);
    setFinalData([]);
  };

  const handleCategoryChange = (option) => {
    setSelectedCategory(option);
    setSelectedItem(null);
    setItemForm(initialItem());
    setItemOptions([]);
    setFinalData([]);
  };

  const updateHeader = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleItemChange = (option) => {
    setSelectedItem(option);
    setItemForm((previous) => ({
      ...previous,
      ItemId: option?.value || "",
      ItemName: option?.label || "",
      AvailableQty: null,
    }));
  };

  const handleAddItem = () => {
    if (!formData.FactoryId) {
      Swal.fire("Validation", "Please select a factory", "warning");
      return;
    }

    if (!itemForm.ItemId) {
      Swal.fire("Validation", "Please select an item.", "warning");
      return;
    }

    const issuedQty = Number(itemForm.IssuedQty);
    const availableQty = Number(itemForm.AvailableQty);

    if (!Number.isFinite(issuedQty) || issuedQty <= 0) {
      Swal.fire("Validation", "Issued quantity must be greater than zero.", "warning");
      return;
    }

    if (issuedQty > availableQty) {
      Swal.fire(
        "Insufficient stock",
        `Available quantity is ${availableQty}. You entered ${issuedQty}.`,
        "warning"
      );
      return;
    }

    const alreadyAddedQty = finalData
      .filter((row) => row.ItemId === itemForm.ItemId)
      .reduce((total, row) => total + Number(row.IssuedQty), 0);

    if (alreadyAddedQty + issuedQty > availableQty) {
      Swal.fire(
        "Insufficient stock",
        `This item already has ${alreadyAddedQty} entered. Total issued quantity cannot exceed ${availableQty}.`,
        "warning"
      );
      return;
    }

    setFinalData((previous) => [
      ...previous,
      {
        lineId: `${itemForm.ItemId}-${Date.now()}-${previous.length}`,
        ItemId: itemForm.ItemId,
        ItemName: itemForm.ItemName,
        AvailableQty: availableQty,
        IssuedQty: issuedQty,
        IssuedAmt: 0,
        PlaceofUse: itemForm.PlaceofUse.trim(),
        ItemRemark: itemForm.ItemRemark.trim(),
      },
    ]);

    setItemForm(initialItem());
  };

  const handleDeleteItem = async (lineId) => {
    const result = await Swal.fire({
      title: "Remove this item?",
      text: "This item will be removed from the voucher.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setFinalData((previous) => previous.filter((row) => row.lineId !== lineId));
    }
  };

  const validateVoucher = () => {
    if (!formData.IssueDate) return "Issue date is required.";
    if (!formData.IssueVoucherNo.trim()) return "Voucher number is required.";
    if (!formData.FactoryId) return "Please select a factory";
    if (formData.SourceType === "Material Request" && !formData.ReferenceId) {
      return "Please select a material request.";
    }
    if (finalData.length === 0) return "Please add at least one item.";
    return "";
  };

  const handleFinalSubmit = async (event) => {
    event.preventDefault();
    if (isViewMode) return;

    const validationMessage = validateVoucher();
    if (validationMessage) {
      Swal.fire("Validation Error", validationMessage, "warning");
      return;
    }

    const payload = {
      ...formData,
      IssueVoucherNo: formData.IssueVoucherNo.trim(),
      IssueTo: formData.IssueTo.trim(),
      IssueRemark: formData.IssueRemark.trim(),
      IssueNarration: formData.IssueNarration.trim(),
      ReferenceId:
        formData.SourceType === "Direct" ? null : formData.ReferenceId,
      items: finalData.map((item) => ({
        ItemId: item.ItemId,
        IssuedQty: Number(item.IssuedQty),
        PlaceofUse: item.PlaceofUse,
        ItemRemark: item.ItemRemark,
      })),
    };

    setSaving(true);
    try {
      const response = await api.post("/inventory/stock-out", payload);

      await Swal.fire(
        "Success",
        response.data?.message || "Consumption voucher saved successfully.",
        "success"
      );

      setSelectedFactory(null);
      setSelectedCategory(null);
      setSelectedItem(null);
      setFormData(initialHeader());
      setItemForm(initialItem());
      setFinalData([]);
      setItemOptions([]);
    } catch (error) {
      console.error("Failed to save consumption voucher:", error);
      const message =
        error.response?.data?.message ||
        "Failed to save consumption voucher. Please check the API route and server logs.";
      Swal.fire("Error", message, "error");
    } finally {
      setSaving(false);
    }
  };

  const totalIssuedQty = useMemo(() => {
    return finalData.reduce(
      (total, item) => total + Number(item.IssuedQty || 0),
      0
    );
  }, [finalData]);

  const totalIssuedAmt = useMemo(() => {
    return finalData.reduce(
      (total, item) => total + Number(item.IssuedAmt || 0),
      0
    );
  }, [finalData]);
  const filteredItemOptions = useMemo(() => {
  const addedItemIds = new Set(finalData.map((row) => row.ItemId));
  return itemOptions.filter((opt) => !addedItemIds.has(opt.value));
}, [itemOptions, finalData]);

  const inputClass =
    "w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed";

  return (
    <form onSubmit={handleFinalSubmit} className="mx-auto w-[95%] pb-8">
      {/* Top Navbar */}
      <div className="mb-4 mt-5 flex h-11 items-center justify-between rounded-md bg-white px-3 shadow">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold text-violet-600">
            {isViewMode ? "VIEW CONSUMPTION VOUCHER" : "NEW CONSUMPTION FORM"}
          </h1>
          {isViewMode && (
            <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-violet-700">
              View Mode
            </span>
          )}
        </div>
        <BackBtn />
      </div>

      {loadingDetail ? (
        <div className="flex flex-col items-center justify-center rounded-md bg-white p-12 shadow-sm">
          <div className="h-9 w-9 animate-spin rounded-full border-4 border-violet-200 border-t-violet-600"></div>
          <p className="mt-3 text-sm font-medium text-gray-600">
            Loading consumption voucher details...
          </p>
        </div>
      ) : (
        <>
          {/* Section 1: Voucher Details */}
          <section className="mb-5 rounded-md bg-white p-4 shadow-sm">
            <h2 className="mb-4 border-b pb-2 font-semibold text-gray-700">
              Voucher Details
            </h2>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {/* Issue Date */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Issue Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.IssueDate}
                  onChange={(event) =>
                    updateHeader("IssueDate", event.target.value)
                  }
                  className={inputClass}
                  disabled={isViewMode}
                  required
                />
              </div>

              {/* Voucher Number */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Voucher No. <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.IssueVoucherNo}
                  onChange={(event) =>
                    updateHeader("IssueVoucherNo", event.target.value)
                  }
                  className={inputClass}
                  placeholder="Enter voucher number"
                  disabled={isViewMode}
                  required
                />
              </div>

              {/* Factory (Displayed in header if in View Mode) */}
              {isViewMode && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Factory
                  </label>
                  <input
                    type="text"
                    value={selectedFactory?.label || "—"}
                    className={inputClass}
                    disabled
                  />
                </div>
              )}

              {/* Issue Tag */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Issue Tag
                </label>
                <Select
                  isDisabled={isViewMode}
                  options={[
                    { value: "Regular", label: "Regular" },
                    {
                      value: "Repair-Maintenance",
                      label: "Repair-Maintenance",
                    },
                  ]}
                  value={{
                    value: formData.IssueTag,
                    label: formData.IssueTag,
                  }}
                  onChange={(option) =>
                    updateHeader("IssueTag", option?.value || "Regular")
                  }
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "38px",
                      borderRadius: "6px",
                      backgroundColor: isViewMode ? "#f3f4f6" : "#ffffff",
                      borderColor: state.isFocused ? "#8b5cf6" : "#d1d5db",
                      boxShadow: state.isFocused
                        ? "0 0 0 1px #8b5cf6"
                        : "none",
                    }),
                  }}
                />
              </div>

              {/* Issue Source */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Issue Source
                </label>
                <Select
                  isDisabled={isViewMode}
                  options={[
                    { value: "Direct", label: "Direct" },
                    { value: "Material Request", label: "Material Request" },
                  ]}
                  value={{
                    value: formData.SourceType,
                    label: formData.SourceType,
                  }}
                  onChange={(option) =>
                    setFormData((previous) => ({
                      ...previous,
                      SourceType: option?.value || "Direct",
                      ReferenceId: null,
                    }))
                  }
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "38px",
                      borderRadius: "6px",
                      backgroundColor: isViewMode ? "#f3f4f6" : "#ffffff",
                      borderColor: state.isFocused ? "#8b5cf6" : "#d1d5db",
                      boxShadow: state.isFocused
                        ? "0 0 0 1px #8b5cf6"
                        : "none",
                    }),
                  }}
                />
              </div>

              {/* Issue To */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Issue To <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.IssueTo}
                  onChange={(event) =>
                    updateHeader("IssueTo", event.target.value)
                  }
                  className={inputClass}
                  placeholder="Person or department"
                  disabled={isViewMode}
                  required
                />
              </div>

              {/* Remark */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Remark
                </label>
                <input
                  type="text"
                  value={formData.IssueRemark}
                  onChange={(event) =>
                    updateHeader("IssueRemark", event.target.value)
                  }
                  className={inputClass}
                  placeholder="Enter remarks"
                  disabled={isViewMode}
                />
              </div>

              {/* Narration */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Narration
                </label>
                <textarea
                  value={formData.IssueNarration}
                  onChange={(event) =>
                    updateHeader("IssueNarration", event.target.value)
                  }
                  className={inputClass}
                  placeholder="Enter narration"
                  rows={1}
                  disabled={isViewMode}
                />
              </div>
            </div>
          </section>

          {/* Section 2: Add Items (Only shown in Add Mode) */}
          {!isViewMode && (
            <section className="mb-5 rounded-md bg-white p-4 shadow-sm">
              <h2 className="mb-4 border-b pb-2 font-semibold text-gray-700">
                Add Items
              </h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                {/* Factory */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Factory <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={factoryOptions}
                    value={selectedFactory}
                    onChange={handleFactoryChange}
                    placeholder="Select factory"
                    isLoading={loadingFactories}
                    isClearable
                    className="text-sm"
                  />
                </div>

                {/* Item Category */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Item Category <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={categoryOptions}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    placeholder="Select category"
                    isLoading={loadingCategories}
                    isClearable
                    className="text-sm"
                  />
                </div>

                {/* Items */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Items <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={filteredItemOptions}
                    value={selectedItem}
                    onChange={handleItemChange}
                    isDisabled={
                      !selectedFactory?.value ||
                      !selectedCategory?.value ||
                      loadingItems
                    }
                    isLoading={loadingItems}
                    isClearable
                    placeholder={
                      !selectedFactory?.value
                        ? "Select factory first"
                        : !selectedCategory?.value
                        ? "Select category first"
                        : "Select item"
                    }
                    formatOptionLabel={(option) => (
                      <div className="flex items-center justify-between gap-3">
                        <span>{option.label}</span>
                        {option.unit && (
                          <span className="text-xs text-gray-500">
                            {option.unit}
                          </span>
                        )}
                      </div>
                    )}
                    className="text-sm"
                  />
                </div>

                {/* Available Quantity */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Available Qty
                  </label>
                  <input
                    type="number"
                    value={itemForm.AvailableQty ?? ""}
                    className={`${inputClass} bg-gray-50`}
                    placeholder={loadingStock ? "" : "0"}
                    readOnly
                  />
                </div>

                {/* Issued Quantity */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Issued Qty <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={itemForm.IssuedQty}
                    onChange={(event) =>
                      setItemForm((previous) => ({
                        ...previous,
                        IssuedQty: event.target.value,
                      }))
                    }
                    className={inputClass}
                    placeholder="Enter quantity"
                  />
                </div>

                {/* Place of Use */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Place of Use
                  </label>
                  <input
                    type="text"
                    value={itemForm.PlaceofUse}
                    onChange={(event) =>
                      setItemForm((previous) => ({
                        ...previous,
                        PlaceofUse: event.target.value,
                      }))
                    }
                    className={inputClass}
                    placeholder="Department / machine / location"
                  />
                </div>

                {/* Item Remark */}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Item Remark
                  </label>
                  <input
                    type="text"
                    value={itemForm.ItemRemark}
                    onChange={(event) =>
                      setItemForm((previous) => ({
                        ...previous,
                        ItemRemark: event.target.value,
                      }))
                    }
                    className={inputClass}
                    placeholder="Optional remark"
                  />
                </div>
              </div>

              {/* Add Item Button */}
              <div className="mt-5 flex justify-end">
                <button
                  type="button"
                  onClick={handleAddItem}
                  disabled={
                    !formData.FactoryId ||
                    !selectedCategory?.value ||
                    !selectedItem ||
                    loadingItems ||
                    loadingStock
                  }
                  className="rounded-md bg-violet-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loadingStock ? "Fetching Stock..." : "+ Add Item"}
                </button>
              </div>
            </section>
          )}

          {/* Section 3: Issued Items Table */}
          <section className="mb-5 overflow-hidden rounded-md bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 p-4">
              <h2 className="font-semibold text-gray-700">
                {isViewMode ? "Voucher Items" : "Issue Items"}
              </h2>

              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>
                  Items:{" "}
                  <strong className="text-gray-900">{finalData.length}</strong>
                </span>

                <span className="h-4 border-l border-gray-300" />

                <span>
                  Total Qty:{" "}
                  <strong className="text-violet-600">
                    {totalIssuedQty.toFixed(3)}
                  </strong>
                </span>

                {isViewMode && totalIssuedAmt > 0 && (
                  <>
                    <span className="h-4 border-l border-gray-300" />
                    <span>
                      Total Amount:{" "}
                      <strong className="text-emerald-600">
                        ₹{totalIssuedAmt.toFixed(2)}
                      </strong>
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] table-auto text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3">SN</th>
                    <th className="px-4 py-3">Item</th>
                    {!isViewMode && (
                      <th className="whitespace-nowrap px-4 py-3 text-right">
                        Available Qty
                      </th>
                    )}
                    <th className="whitespace-nowrap px-4 py-3 text-right">
                      Issued Qty
                    </th>
                    {isViewMode && (
                      <th className="whitespace-nowrap px-4 py-3 text-right">
                        Issued Amt
                      </th>
                    )}
                    <th className="px-4 py-3">Place of Use</th>
                    <th className="px-4 py-3">Remark</th>
                    {!isViewMode && (
                      <th className="px-4 py-3 text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {finalData.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isViewMode ? 6 : 7}
                        className="px-4 py-10 text-center text-gray-500"
                      >
                        No items added yet.
                      </td>
                    </tr>
                  ) : (
                    finalData.map((row, index) => (
                      <tr
                        key={row.lineId || index}
                        className="transition-colors hover:bg-violet-50/40"
                      >
                        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">
                          {row.ItemName}
                        </td>
                        {!isViewMode && (
                          <td className="px-4 py-3 text-right tabular-nums text-gray-600">
                            {Number(row.AvailableQty ?? 0).toFixed(3)}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-900">
                          {Number(row.IssuedQty ?? 0).toFixed(3)}
                        </td>
                        {isViewMode && (
                          <td className="px-4 py-3 text-right font-semibold tabular-nums text-emerald-600">
                            ₹{Number(row.IssuedAmt ?? 0).toFixed(2)}
                          </td>
                        )}
                        <td className="px-4 py-3 text-gray-600">
                          {row.PlaceofUse || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {row.ItemRemark || "—"}
                        </td>
                        {!isViewMode && (
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleDeleteItem(row.lineId)}
                              className="inline-flex items-center justify-center rounded-md p-2 text-red-500 transition hover:bg-red-50 hover:text-red-700"
                              aria-label={`Remove ${row.ItemName}`}
                              title="Remove item"
                            >
                              <Trash2 size={17} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Bottom Action Buttons */}
          <div className="mt-5 flex justify-end gap-3">
            {isViewMode ? (
        ""
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setFormData(initialHeader());
                    setItemForm(initialItem());
                    setFinalData([]);
                    setItemOptions([]);
                    setSelectedFactory(null);
                    setSelectedCategory(null);
                    setSelectedItem(null);
                  }}
                  disabled={saving}
                  className="rounded-md border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={saving || finalData.length === 0}
                  className="rounded-md bg-violet-600 px-6 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Consumption"}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </form>
  );
};

export default NewConsumptionForm;